import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AIChatResponseDto } from "../../model/chat-wiget/dto/AIChatResponseDto";
import { ChatWidgetContentWrapper } from "./ChatWidgetContentWrapper";
import { ChatWidgetCurrent } from "../../model/chat-wiget/types/ChatWigetCurrent.enum";
import { AIFormMessage } from "./AIFormMessage";
import { AIMessagePending } from "./AIMessagePending";
import { Textarea } from "../Textarea";
import { AtiraImage } from "../Image";
import { Button } from "../Button";
import { Images } from "../../assets";
import { Spacing } from "../../theme/Spacing";
import { ChatWidgetAxios } from "../../axios/chat-widget/chat-widget.axios";
import { TestIDs } from "../../tests/types/testIds";
import { AIChatDto } from "../../model/chat-wiget/dto/AIChatDto";
import { useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";

const ChatSection = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  padding: 1.5rem 0.75rem 0.75rem;
`;

const MessagesContainer = styled.div`
  flex-grow: 1;
  overflow-x: hidden;
  max-height: 20rem;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  margin-bottom: 0.75rem;
  min-height: 9rem;
  background-color: #f9fafb;
`;

const InputRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  align-items: center;
`;

const StyledTextarea = styled(Textarea)`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  resize: none;
  min-height: 2.5rem;
  height: auto;
  outline: none;
  background-color: transparent;

  &[dir="rtl"] {
    direction: rtl;
  }
`;

const StyledButton = styled(Button)`
  border-radius: 0.375rem;
  flex-shrink: 0;
  width: fit-content;
  margin: 0;
  height: 3.25rem;
  margin-top: 0.5rem;
  padding-inline: ${Spacing.s};

  html[dir="rtl"] & > div {
    rotate: 180deg;
  }
`;

const StatusText = styled.p<{ error?: boolean }>`
  font-size: 0.875rem;
  margin-top: 0.25rem;
  text-align: center;
  min-height: 1.25rem;
  color: ${(props) => (props.error ? "#ef4444" : "#6b7280")};
`;

type ChatWidgetAIFormProps = {
  onBackAction: VoidFunction;
  buttonAnchor: { left: number; top: number } | null;
};

export const ChatWidgetAIForm: React.FC<ChatWidgetAIFormProps> = ({
  onBackAction,
  buttonAnchor,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Partial<AIChatResponseDto>[]>([]);

  const { t } = useTranslation(undefined, { keyPrefix: "floatingMenu.aiChat" });
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const storageKey = `awc_ai_conversation_history_${userId}`;

  const localOnBackAction = () => {
    setMessage("");
    onBackAction();
  };

  const updateLocalHistory = (message: string, by: AIChatResponseDto["by"]) => {
    const existingHistory = JSON.parse(
      localStorage.getItem(storageKey) || "[]",
    );
    const newHistory = [...existingHistory, { question: message, by }];
    localStorage.setItem(storageKey, JSON.stringify(newHistory));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!loading && message?.length) {
        onSubmit();
      }
    }
  };

  const onSubmit = async () => {
    try {
      setLoading(true);
      setMessages((messages) => [
        ...messages,
        { question: message, by: "user" },
      ]);
      updateLocalHistory(message, "user");
      setMessage("");

      const dto: AIChatDto = {
        question: message,
        pageURL: window.location.href,
        /**
         * Fahmy's past answers are assistant turns, not system instructions.
         * The greeting we seed into the history is a UI message with no
         * answer, so it is dropped - the website has no such greeting, and
         * sending it made the app drift away from the website's answers.
         */
        history: messages
          .filter((msg) => (msg.by === "user" ? msg.question : msg.answer))
          .map((msg) => ({
            role: (msg.by === "user" ? "user" : "assistant") as
              | "user"
              | "assistant",
            content: msg.question || msg.answer || "",
          })),
      };

      const data = await ChatWidgetAxios.chatAI(dto);

      updateLocalHistory(data.answer, "system");

      setMessages((messages) => [
        ...messages,
        { answer: data.answer, by: "system" },
      ]);

      setError(false);
    } catch (e: any) {
      console.error(e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const displayHistory = () => {
      const messages = JSON.parse(
        localStorage.getItem(storageKey) || "[]",
      ) as AIChatResponseDto[];

      if (messages.length === 0) {
        const firstSystemMessage: Partial<AIChatResponseDto> = {
          question: t("defaultSystemMessage"),
          by: "system",
        };
        setMessages([firstSystemMessage]);
        localStorage.setItem(storageKey, JSON.stringify([firstSystemMessage]));
      } else {
        setMessages(
          messages.map((msg: Partial<AIChatResponseDto>) => ({
            question: msg.question,
            answer: msg.answer,
            by: msg.by,
          })),
        );
      }
    };
    displayHistory();
  }, [t]);

  useEffect(() => {
    const messagesArea = document.getElementById("ai-chat-messages");
    const lastMessage = messagesArea?.lastElementChild;
    if (lastMessage) {
      lastMessage.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [messages.length]);

  return (
    <ChatWidgetContentWrapper
      onBackAction={localOnBackAction}
      current={ChatWidgetCurrent.CHAT}
      dataTestId={TestIDs.fahmy.content.chat.wrapper}
      buttonAnchor={buttonAnchor}
    >
      <ChatSection>
        <MessagesContainer id="ai-chat-messages">
          {messages?.map((message, index) => (
            <AIFormMessage
              key={`${message.question || message.answer}-${message.by}-${index}`}
              message={message as AIChatResponseDto}
            />
          ))}
          {loading && <AIMessagePending />}
        </MessagesContainer>

        <InputRow>
          <StyledTextarea
            id="awc-ai-chat-input"
            placeholder={t("inputPlaceholder")}
            value={message}
            onChange={(e) => setMessage(e.currentTarget.value)}
            onKeyDown={handleKeyPress}
            autoFocus
            dir="auto"
          />
          <StyledButton
            onClick={onSubmit}
            disabled={!message?.length || loading}
          >
            <AtiraImage
              src={Images.WidgetMessageSendIcon}
              width="20px"
              height="20px"
            />
          </StyledButton>
        </InputRow>

        {error ? <StatusText error={error}>{t("error")}</StatusText> : null}
      </ChatSection>
    </ChatWidgetContentWrapper>
  );
};
